/**
 * VoiceCallProvider — global context provider for softphone call state.
 *
 * Bridges the provider-agnostic `voiceManager` facade (Twilio or Telnyx, chosen at runtime from the
 * voice token's `provider`) into the React component tree.
 * Place this near the root of the app (inside auth + company providers).
 */

'use client';

import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Phone } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { voiceManager } from '@/lib/voiceManager';
import { telnyxVoiceWebManager } from '@/lib/telnyxVoiceWeb';
import { EMERGENCY_CALL_MESSAGE, isEmergencyNumber } from '@/lib/phoneUtils';
import type { AudioDeviceType, VoiceCallEvent } from '@/lib/twilioVoice';
import { VoiceCallContext, VoiceCallContextValue } from '@/hooks/useVoiceCall';
import { ActiveCallOverlay } from '@/components/ActiveCallOverlay';
import { FloatingCallWidget } from '@/components/FloatingCallWidget';
import { IncomingCallModal } from '@/components/IncomingCallModal';
import { IncomingCallBanner } from '@/components/IncomingCallBanner';
import { JobDetailsModal } from '@/components/JobDetailsModal';
import { EstimateDetailsModal } from '@/components/EstimateDetailsModal';
import { WebDialerReceiveIndicator } from '@/components/WebDialerReceiveIndicator';
import { useNotifications, notificationService } from '@/components/ui/NotificationSystem';
import { registerBackButtonHandler } from '@/lib/capacitor';
import { apiService, apiErrorMessage } from '@/lib/api';

interface VoiceCallProviderProps {
  children: ReactNode;
}

/** Height of the persistent call bar; the app content is pushed down by this much while it shows. */
const CALL_BAR_HEIGHT = 52;

function formatCallDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function VoiceCallProvider({ children }: VoiceCallProviderProps) {
  const [event, setEvent] = useState<VoiceCallEvent>({ state: 'idle' });
  const [callDuration, setCallDuration] = useState(0);
  // When true, the full-screen overlay is hidden but the call continues; the user can return to it
  // via the floating pill (or the foreground-service notification re-opening the app).
  const [minimized, setMinimized] = useState(false);
  // Job/estimate opened from a call widget, conversion, etc. This provider sits under the Router
  // and the query/company providers, so it can host the overlay itself — callers have no route to push.
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [openEstimateId, setOpenEstimateId] = useState<string | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { addNotification } = useNotifications();

  // ── Subscribe to manager events ─────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = voiceManager.subscribe((e) => setEvent(e));
    return unsubscribe;
  }, []);

  const isActive = event.state !== 'idle';

  // Reset the minimized state once no call is active, so the next call opens the overlay fresh.
  useEffect(() => {
    if (!isActive) setMinimized(false);
  }, [isActive]);

  // While the overlay is showing, the Android back button should just minimize it — never navigate
  // the app underneath (which would leave the job screen) and never hang up the call. The persistent
  // call bar (below) is how the user returns to the softphone.
  useEffect(() => {
    if (!isActive || minimized) return;
    const unregister = registerBackButtonHandler(() => setMinimized(true));
    return unregister;
  }, [isActive, minimized]);

  // While the call bar is up, reserve space at the top of every screen (.screen-container and
  // .safe-area-top containers add --call-bar-offset to their top padding) so the bar pushes the
  // header down instead of overlapping it.
  useEffect(() => {
    if (!(isActive && minimized)) return;
    document.documentElement.style.setProperty('--call-bar-offset', `${CALL_BAR_HEIGHT}px`);
    return () => document.documentElement.style.setProperty('--call-bar-offset', '0px');
  }, [isActive, minimized]);

  // ── Call duration timer ─────────────────────────────────────────────────────
  // Keyed on callSid as well as state: switching between two calls keeps the state at 'connected',
  // so without that the timer would keep counting the OTHER call's elapsed time. The primary's own
  // connectedAt (when the roster reports one) is what the count restarts from.
  const primaryCall = event.calls?.find((c) => c.isPrimary);
  const primaryConnectedAt = primaryCall?.connectedAt;
  useEffect(() => {
    if (event.state === 'connected') {
      const seed = primaryConnectedAt
        ? Math.max(0, Math.floor((Date.now() - primaryConnectedAt) / 1000))
        : 0;
      setCallDuration(seed);
      const startedAt = Date.now() - seed * 1000;
      durationTimerRef.current = setInterval(() => {
        setCallDuration(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
      }, 1000);
    } else {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
      if (event.state === 'idle') setCallDuration(0);
    }
    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
    };
  }, [event.state, event.callSid, primaryConnectedAt]);

  // Outbound calls stay one-at-a-time: call waiting adds an inbound second line only, so every
  // dial entry point (dial pad, job/contact actions, messaging, call sheet) is still refused while
  // anything is live — including a call parked on hold.
  const callInProgress = useCallback((): boolean => {
    if (voiceManager.currentState.state !== 'idle') {
      addNotification(notificationService.error('Call in progress', 'End the current call before starting a new one.'));
      return true;
    }
    return false;
  }, [addNotification]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const makeCall = useCallback(
    async (toE164: string, callerIdE164: string, displayName?: string, jobId?: string) => {
      if (callInProgress()) return;
      // Every call-launch path (dialer, CallLauncher, CallMethodSheet, the messaging call button)
      // funnels through here, so this one guard covers all of them. Purchased numbers have no
      // emergency service — refusing plainly is defensible, a 911 call that quietly fails is not.
      if (isEmergencyNumber(toE164)) {
        addNotification(notificationService.error('Emergency Calls Not Supported', EMERGENCY_CALL_MESSAGE));
        return;
      }
      try {
        await voiceManager.makeCall(toE164, callerIdE164, displayName, jobId);
      } catch (err: unknown) {
        // Managers that got far enough emit on the call widget; toast only when the failure
        // happened before that (e.g. voice-minute gate on the token endpoint).
        if (voiceManager.currentState.state === 'idle') {
          addNotification(notificationService.error(
            'Call Failed',
            apiErrorMessage(err, 'Failed to start the call. Please try again.'),
          ));
        }
      }
    },
    [callInProgress, addNotification],
  );

  const makeMaskedCall = useCallback(
    async (jobId: string, contactId: string, which: 'primary' | 'additional' = 'primary', displayName?: string, chosenCallerId?: string) => {
      if (callInProgress()) return;
      try {
        // The server limits a technician to the numbers technicians can use and puts the job's line first.
        const callerIdE164 = chosenCallerId
          ?? (await apiService.getPhoneNumbers(jobId)).find((n) => n.capabilitiesVoice && n.isActive)?.e164Number;
        if (!callerIdE164) {
          addNotification(notificationService.error('Call Failed', 'No company number is available for this call.'));
          return;
        }
        await voiceManager.makeMaskedCall(jobId, contactId, callerIdE164, which, displayName);
      } catch (err: unknown) {
        if (voiceManager.currentState.state === 'idle') {
          addNotification(notificationService.error(
            'Call Failed',
            apiErrorMessage(err, 'Failed to start the call. Please try again.'),
          ));
        }
      }
    },
    [addNotification],
  );

  const endCall = useCallback(async () => {
    await voiceManager.endCall(event.callSid);
  }, [event.callSid]);

  const redial = useCallback(async () => {
    await voiceManager.redial();
  }, []);

  const toggleMute = useCallback(async () => {
    await voiceManager.muteCall(!event.isMuted, event.callSid);
  }, [event.isMuted, event.callSid]);

  const selectAudioDevice = useCallback(async (type: AudioDeviceType, id?: string) => {
    await voiceManager.selectAudioDevice(type, id);
  }, []);

  const sendDtmf = useCallback((digit: string) => {
    voiceManager.sendDigits(digit);
  }, []);

  // ── Call waiting ────────────────────────────────────────────────────────────
  // These talk to the web manager directly, like IncomingCallModal already does: concurrent calls
  // are a web-only capability (the native SDK cannot hold a second call at all), so routing them
  // through the provider-agnostic facade would mean adding a surface no other backend implements.
  const answerCall = useCallback(async (callSid: string) => {
    await telnyxVoiceWebManager.answerIncoming(callSid);
  }, []);

  const declineCall = useCallback((callSid: string) => {
    telnyxVoiceWebManager.rejectIncoming(callSid);
  }, []);

  const setHold = useCallback(async (callSid: string, held: boolean) => {
    await telnyxVoiceWebManager.setHold(callSid, held);
  }, []);

  const endCallById = useCallback(async (callSid: string) => {
    await voiceManager.endCall(callSid);
  }, []);

  const openJob = useCallback((jobId: string) => {
    setOpenEstimateId(null);
    setOpenJobId(jobId);
  }, []);
  const closeJob = useCallback(() => setOpenJobId(null), []);
  const openEstimate = useCallback((estimateId: string) => {
    setOpenJobId(null);
    setOpenEstimateId(estimateId);
  }, []);
  const closeEstimate = useCallback(() => setOpenEstimateId(null), []);

  // ── Context value ────────────────────────────────────────────────────────────
  const value: VoiceCallContextValue = {
    callState: event.state,
    remoteNumber: event.toE164 ?? '',
    remoteName: event.displayName,
    callerIdE164: event.callerIdE164,
    callSid: event.callSid,
    isMuted: event.isMuted ?? false,
    audioRoute: event.audioRoute ?? 'earpiece',
    audioRouteError: event.audioRouteError,
    availableAudioDevices: event.availableAudioDevices ?? [],
    qualityWarnings: event.qualityWarnings ?? [],
    callDuration,
    error: event.error,
    canRedial: event.canRedial,
    calls: event.calls ?? [],
    makeCall,
    makeMaskedCall,
    endCall,
    redial,
    toggleMute,
    selectAudioDevice,
    sendDtmf,
    answerCall,
    declineCall,
    setHold,
    endCallById,
    openJob,
    closeJob,
    openEstimate,
    closeEstimate,
  };

  // Avoid surfacing the opaque masked-call reference ("contact:…") in the bar.
  const barLabel =
    event.displayName ||
    (event.toE164 && !event.toE164.startsWith('contact:') ? event.toE164 : 'Ongoing call');
  const barStatus =
    event.state === 'connected' ? formatCallDuration(callDuration) :
    event.state === 'ringing' ? 'Ringing…' :
    event.state === 'reconnecting' ? 'Reconnecting…' :
    event.state === 'incoming' ? 'Incoming call…' :
    event.state === 'ended' ? 'Call ended' :
    'Connecting…';

  // On web the softphone is a compact bottom-right widget (which owns its own minimize state); native
  // keeps the full-screen overlay + top call bar. The web softphone is desktop-only (mobile browsers
  // never place calls), so no mobile-web overlay path is needed here.
  const useDesktopWidget = !Capacitor.isNativePlatform();
  // Incoming ring gets its own card (Answer/Decline) — the call widget takes over once answered.
  const isIncoming = event.state === 'incoming';
  // A SECOND call ringing while the first is up (call waiting). It never becomes the primary until
  // answered, so it gets the compact banner instead of the full-screen modal + ringtone.
  const waitingCall = event.calls?.find((c) => c.status === 'incoming' && !c.isPrimary);

  return (
    <VoiceCallContext.Provider value={value}>
      {useDesktopWidget && isIncoming && (
        <IncomingCallModal
          callerNumber={event.toE164 ?? ''}
          context={event.incomingContext}
          callSid={event.callSid}
        />
      )}
      {useDesktopWidget && isActive && !isIncoming && <FloatingCallWidget />}
      {/* Call waiting: rendered ALONGSIDE the widget (not instead of it) — the user must keep the
          controls for the call they're on while deciding about the new one. */}
      {useDesktopWidget && waitingCall && (
        <IncomingCallBanner
          key={waitingCall.callSid}
          call={waitingCall}
          onAnswer={() => { void answerCall(waitingCall.callSid); }}
          onDecline={() => declineCall(waitingCall.callSid)}
        />
      )}
      {/* This tab's receive status pill + minimal menu (owner decision: the corner UI stays
          minimal — the full routing editor lives in Settings → VoIP; the pill renders nothing
          when receive mode is off). Shares the corner with the call UI, so idle-only. */}
      {useDesktopWidget && !isActive && <WebDialerReceiveIndicator />}
      {/* Persistent call bar (WhatsApp-style) shown while a call is minimized. Portalled to <body> so
          it escapes any in-app stacking context and is never covered by a screen header/overlay. */}
      {!useDesktopWidget && isActive && minimized && createPortal(
        <button
          onClick={() => setMinimized(false)}
          aria-label="Return to call"
          className="fixed inset-x-0 top-0 w-full bg-green-600 text-left text-white shadow-md transition-colors active:bg-green-700"
          // Max z-index so no modal/overlay can ever paint over it. paddingTop drops the content below
          // the status bar; total height (inset + CALL_BAR_HEIGHT) matches the space reserved on screen.
          style={{
            zIndex: 2147483647,
            paddingTop: 'max(env(safe-area-inset-top, 0px), var(--android-safe-area-top, 0px))',
          }}
        >
          <div className="flex items-center gap-3 px-4" style={{ height: `${CALL_BAR_HEIGHT}px` }}>
            <Phone className="h-5 w-5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-tight">{barLabel}</p>
              <p className="text-xs leading-tight text-green-100">{barStatus} · tap to return</p>
            </div>
          </div>
        </button>,
        document.body,
      )}
      {/* Job/estimate opened from a call widget, conversion, etc. Mounted here (under the Router
          + data providers) rather than in the portalled widget, which has no route to navigate to.
          Each modal self-fetches from the id and owns its loading / 404 / offline states. */}
      {openJobId && (
        <JobDetailsModal jobId={openJobId} isOpen onClose={() => setOpenJobId(null)} />
      )}
      {openEstimateId && (
        <EstimateDetailsModal estimateId={openEstimateId} isOpen onClose={() => setOpenEstimateId(null)} />
      )}
      {children}
      {!useDesktopWidget && isActive && !minimized && <ActiveCallOverlay onMinimize={() => setMinimized(true)} />}
    </VoiceCallContext.Provider>
  );
}
